import type { FC, PropsWithChildren } from "react";
import React from "react";
import { useBrand } from "@edreamsodigeo/frontend-core";
import {
  BackgroundImage,
  Body,
  Box,
  Button,
  Flex,
  Glyph,
  Heading,
  Pill,
} from "@edreamsodigeo/prisma-design-system";

import PrimeLogo from "~/public/PrimeLogo";
import { primeHooverBannerBrandConfig } from "./constants/styles";
import type { PrimeHooverBannerTemplateProps } from "./contract";

const PrimeHooverBannerTemplate: FC<
  PropsWithChildren<PrimeHooverBannerTemplateProps>
> = ({
  header,
  logo,
  pill,
  title,
  description,
  image,
  button,
  offer,
  children,
}) => {
  const brand = useBrand();

  const theme = primeHooverBannerBrandConfig[brand];

  return (
    <Box
      borderTopLeftRadius={2}
      borderTopRightRadius={2}
      backgroundColor={theme.bgColor}
      width={"520px"}
      overflow={"hidden"}
    >
      <BackgroundImage
        data-testid={`background-${brand}`}
        backgroundImage={`url(${theme.bgImage})`}
        width={"100%"}
        height={header ? "260px" : "100%"}
        backgroundRepeat={"no-repeat"}
        backgroundPosition={"center"}
        backgroundSize={header ? "auto 230%" : "auto 124%"}
      >
        {header && (
          <Flex
            justifyContent={"space-between"}
            paddingY={"small"}
            paddingX={"big"}
            alignItems={"center"}
            backgroundColor={theme.headerBgColor}
            sx={{ background: theme.headerBgColor }}
          >
            <Flex gap={"2"}>
              <Glyph
                category={"Deals"}
                name={"CheckDeal"}
                color={theme.titleColor}
              />
              <Body type={"small"} color={theme.textColor}>
                {header}
              </Body>
            </Flex>
            <PrimeLogo type={"short"} size={"xs"} negative />
          </Flex>
        )}
        <Box px={"1x_big"} py={header ? "big" : "1x_big"}>
          <Flex alignItems={"end"}>
            <Flex flexDirection={"column"} flex={1} mr={"small"} gap={"base"}>
              {logo && (
                <Box mb={"1x_small"}>
                  <PrimeLogo type={"short"} size={"s"} negative />
                </Box>
              )}

              {pill && (
                <Box>
                  <Pill type={"promotional"} rounded>
                    <Body type={"xSmall"} sx={{ lineHeight: "inherit" }}>
                      {pill}
                    </Body>
                  </Pill>
                </Box>
              )}

              <Heading
                type={title.format === "large" ? "2" : "3"}
                color={theme.titleColor}
                weight={"medium"}
                textAlign={title.align}
                sx={{ minWidth: title.minWidth }}
              >
                {title.text}
              </Heading>

              <Body
                type={"small"}
                color={theme.textColor}
                sx={{ minWidth: description.minWidth }}
              >
                {description.text}
              </Body>
              {offer && (
                <Body
                  type={"xSmall"}
                  color={theme.textColor}
                  sx={{ minWidth: offer.minWidth }}
                >
                  {offer.component}
                </Body>
              )}
            </Flex>
            {image && (
              <Box
                marginRight={image.marginRight}
                marginBottom={image.marginBottom}
                marginTop={image.marginTop}
                marginLeft={image.marginLeft}
                height={image.height}
              >
                <img src={image.src} alt={image.alt} height={"100%"} />
              </Box>
            )}
          </Flex>
        </Box>
      </BackgroundImage>
      <Flex
        flexDirection={"column"}
        backgroundColor={"neutral.0"}
        padding={"1x_big"}
        alignItems={button.align}
      >
        {children && (
          <Box
            marginTop={"minus_1x_huge"}
            marginBottom={"small"}
            width={"100%"}
          >
            {children}
          </Box>
        )}
        <Box>
          <Button onClick={button.onClick} size={"medium"} type={"primary"}>
            {button.text}
          </Button>
        </Box>
      </Flex>
    </Box>
  );
};

export default PrimeHooverBannerTemplate;
